import { AHText, Box } from "arkh";
import React, { Suspense, useState, useEffect, useRef } from "react";
import { FormattedMessage } from "react-intl";
import { ActivityIndicator } from "react-native";

export function WebPreview({
  isDirty,
  publicUrl,
  saveKey,
}: {
  isDirty: boolean;
  publicUrl: string;
  saveKey: number;
}) {
  return (
    <Box
      style={{
        flex: 1,
        height: "100%",
        flexDirection: "column",
      }}
      color="surface-backgroundControl"
    >
      <Box
        color="darkerText"
        style={{
          width: "100%",
          height: 30,
          backgroundColor: "#343437",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <AHText textAlign="center" type="subtitle" color="text-inverted">
          {isDirty ? (
            <FormattedMessage
              defaultMessage="Unsaved changes, save to view changes"
              description="Connect Admin Inbox: Preview header dirty text"
            />
          ) : (
            <FormattedMessage
              defaultMessage="Preview"
              description="Connect Admin Inbox: Preview header clean text"
            />
          )}
        </AHText>
      </Box>
      <Box
        style={{
          flex: 1,
          flexDirection: "row",
          justifyContent: "space-around",
        }}
        gap="large"
        space="large"
      >
        <Box
          style={{
            maxWidth: 400,
            maxHeight: 700,
            overflow: "hidden",
            minWidth: 300,
            flex: 1,
          }}
          cornerRadius="medium"
          shadow="strong"
          color="surface"
        >
          <IframeWithRefresh src={publicUrl + "?key=" + saveKey} />
        </Box>
        <Box
          style={{
            maxWidth: 400,
            maxHeight: 700,
            overflow: "hidden",
            minWidth: 300,
            flex: 1,
          }}
          cornerRadius="medium"
          shadow="strong"
          color="surface"
        >
          <Suspense fallback={<ActivityIndicator />}>
            <IframeWithRefresh src={publicUrl + "/success?key=" + saveKey} />
          </Suspense>
        </Box>
      </Box>
    </Box>
  );
}
function IframeWithRefresh({ src }: { src: string }) {
  const [urls, setUrls] = useState<{ url: string; isLoaded: boolean }[]>([
    { url: src, isLoaded: true },
  ]);
  const prevSrcRef = useRef(src);

  useEffect(() => {
    if (prevSrcRef.current !== src) {
      prevSrcRef.current = src;
      setUrls((prev) => [{ url: src, isLoaded: false }, ...prev]);
    }
  }, [src]);

  const handleLoad = (loadedUrl: string) => {
    setTimeout(() => {
      setUrls((prev) => {
        const loadedIndex = prev.findIndex((u) => u.url === loadedUrl);
        if (loadedIndex === -1) return prev;

        // Check if a newer iframe (lower index) is already loaded
        const newerLoadedExists = prev
          .slice(0, loadedIndex)
          .some((u) => u.isLoaded);

        if (newerLoadedExists) {
          // Newer content already shown, remove this stale iframe
          return prev.filter((u) => u.url !== loadedUrl);
        }

        // This is the freshest loaded iframe, keep it and remove older ones
        return prev
          .slice(0, loadedIndex + 1)
          .map((u, i) => (i === loadedIndex ? { ...u, isLoaded: true } : u));
      });
    }, 800);
  };

  return (
    <Suspense fallback={<ActivityIndicator />}>
      {urls.map(({ url, isLoaded }) => (
        <iframe
          key={url}
          src={url}
          onLoad={() => handleLoad(url)}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            border: 0,
            opacity: isLoaded ? 1 : 0,
          }}
        />
      ))}
    </Suspense>
  );
}
